import { useMemo, useRef, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle, LineChart, StackedBarChart } from '@mesh.ai/rabbit';
import OverviewSection from 'Deer/components/Overview/OverviewSection';
import { ONE_ON_ONE_STATUS_NODES } from 'Deer/hooks/dataHooks/constants';
import { useGetOverviewMetrics } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetrics';
import { useGetOverviewMetricsExplanations } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetricsExplanations';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import { useSelectVisierAuthToken } from 'Deer/slice/UserAccessesSlice';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { checkSandboxStaticDataFeaturesEnabled, checkNewStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

import { transformedCardsForOneOnOne } from '../../utlis';
import { DeerBarChart } from '../common/DeerBarChart';
import { DeerLineChart } from '../common/DeerLineChart';
import { barChartData, cardsData, lineChartData } from './oneOnOneMockData';

export const DirectReportsOneOnOneOverview = () => {
  const barChartRef = useRef();
  const lineChartRef = useRef();
  const history = useHistory();
  const dispatch = useDispatch();
  const currentURL = history.location.pathname;
  const visierAuthToken = useSelectVisierAuthToken();
  const localStorageKey = getUniqueCardKey({ url: currentURL, cardId: 'myDirectTeamOneOnOneOverview' });

  const isCardOpen = useLocalStorageCards({
    localStorageKey,
    card: 'oneOnOneOverview',
    section: 'myDirectTeam',
  });
  const currentDate = new Date();

  const formattedDate = format(currentDate, 'yyyy-MM-dd');

  const [correlationId] = useState(window.crypto.randomUUID());

  const { data, isLoading, isError, isFetched } = useGetOverviewMetrics<
    ['ONE_ON_ONE_AGENDA_TOPIC_DISTRIBUTION', 'ONE_ON_ONE_COUNT_BY_STATUS']
  >({
    metrics: [
      {
        analyticsType: 'ONE_ON_ONE_V2',
        metricType: 'ONE_ON_ONE_AGENDA_TOPIC_DISTRIBUTION',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: 6,
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
      {
        analyticsType: 'ONE_ON_ONE_V2',
        metricType: 'ONE_ON_ONE_COUNT_BY_STATUS',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: 6,
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
    ],
    cohortId: -2,
    group: 'ONE_ON_ONE',
    correlationId,
  });

  const { data: kpiCardData } = useGetOverviewMetrics<
    ['ONE_ON_ONE_COMPLETION_PERCENTAGE_AVERAGE_KPI', 'EMP_COUNT_NO_OOO_COMPLETED', 'EMP_COUNT_NO_OOO_SCHEDULED']
  >({
    metrics: [
      {
        analyticsType: 'ONE_ON_ONE_V2',
        metricType: 'ONE_ON_ONE_COMPLETION_PERCENTAGE_AVERAGE_KPI',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: 1,
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
      {
        analyticsType: 'ONE_ON_ONE_V2',
        metricType: 'EMP_COUNT_NO_OOO_SCHEDULED',
      },
      {
        analyticsType: 'ONE_ON_ONE_V2',
        metricType: 'EMP_COUNT_NO_OOO_COMPLETED',
      },
    ],
    cohortId: -2,
  });

  const cardsDataFromApi = useMemo(
    () =>
      kpiCardData &&
      transformedCardsForOneOnOne({
        ONE_ON_ONE_COMPLETION_PERCENTAGE_AVERAGE_KPI: kpiCardData.ONE_ON_ONE_COMPLETION_PERCENTAGE_AVERAGE_KPI,
        EMP_COUNT_NO_OOO_COMPLETED: kpiCardData.EMP_COUNT_NO_OOO_COMPLETED,
        EMP_COUNT_NO_OOO_SCHEDULED: kpiCardData.EMP_COUNT_NO_OOO_SCHEDULED,
      }),
    [kpiCardData]
  );

  const { data: explanationData } = useGetOverviewMetricsExplanations({
    cohortId: -2,
    group: 'ONE_ON_ONE',
    correlationId,
    enabled: isFetched,
  });

  const explanation: {
    explanation?: string;
    sentiment?: 'warning' | 'success';
  } = useMemo(
    () =>
      explanationData && explanationData[0]
        ? {
            explanation: explanationData[0].explanation,
            sentiment: explanationData[0].sentiment === 'negative' ? 'warning' : 'success',
          }
        : {},
    [explanationData]
  );

  const handleChange = (newVal: boolean) => {
    dispatch(
      setIsCardOpen({
        card: 'oneOnOneOverview',
        section: 'myDirectTeam',
        isCardOpen: newVal,
        localStorageKey,
      })
    );
    return newVal;
  };

  return (
    <OverviewSection
      isError={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? false : !visierAuthToken
      }
      open={isCardOpen}
      onOpenChange={handleChange}
      title="1:1s"
      showCaption={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() || explanation.explanation
      }
      subTitleColor={explanation.sentiment ?? 'warning'}
      subTitle={
        explanation.explanation ??
        "It appears you're canceling more 1:1s than conducting them, suggesting challenges in determining discussion topics. For instance, Growth hasn't been discussed with your teammates. Consider leveraging Maven's recommended agenda topics for productive 1:1s."
      }
      topSection={
        <MetricCards
          columns={`repeat(${
            checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
              ? cardsData?.length
              : cardsDataFromApi?.length
          }, 1fr)`}
          cards={
            checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
              ? cardsData
              : cardsDataFromApi
          }
          cardTitle="Metrics"
        />
      }
      emptyStateDescription="Insights on your team's 1:1 performance would appear here once they are setup"
    >
      {/* 1-1 completion/cancellation Chart */}
      <Card
        style={{
          marginBottom: '8px',
        }}
      >
        <CardHeader>
          <CardTitle>Completion</CardTitle>
        </CardHeader>
        <CardContent>
          {checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? (
            <div style={{ height: '240px' }} className="rabbit-ds">
              <StackedBarChart
                ref={barChartRef}
                mode="full"
                nodes={barChartData?.nodes}
                series={barChartData?.series}
                tooltipTitle="Completion"
                xAxisLabel="Team members"
                xAxisValues={barChartData?.xaxis}
                yAxisLabel="1:1 meeting count"
                seriesSelectorTitle="Status"
                maxNodesInMiniMode={0}
              />
            </div>
          ) : (
            <DeerBarChart
              isLoading={isLoading}
              isError={isError}
              ref={barChartRef}
              nodes={ONE_ON_ONE_STATUS_NODES}
              series={data?.ONE_ON_ONE_COUNT_BY_STATUS.series}
              tooltipTitle="Completion"
              xAxisLabel="Team members"
              xAxisValues={data?.ONE_ON_ONE_COUNT_BY_STATUS.xAxis}
              yAxisLabel="1:1 meeting count"
              seriesSelectorTitle="Status"
              hasNoData={data?.ONE_ON_ONE_COUNT_BY_STATUS?.xAxis?.length === 0}
              emptyStateDescription="Insights on your team's 1:1 performance would appear here once they are setup"
            />
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Topic distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? (
            <div style={{ height: '222px' }} className="rabbit-ds">
              <LineChart
                ref={lineChartRef}
                nodes={lineChartData.nodes}
                xAxisValues={lineChartData.xaxis}
                series={lineChartData.series}
                xAxisLabel="Months"
                yAxisLabel="Team covered %"
                mode="full"
                connectNulls
                yAxisDomain={[lineChartData.yaxisRange.start, lineChartData.yaxisRange.end]}
                tooltipTitle="Topic"
                seriesSelectorTitle="Agenda topics"
                maxNodesInMiniMode={0}
                yAxisUnitSymbol={{
                  position: 'post',
                  symbol: '%',
                }}
              />
            </div>
          ) : (
            <DeerLineChart
              ref={lineChartRef}
              isLoading={isLoading}
              isError={isError}
              hasNoData={data?.ONE_ON_ONE_AGENDA_TOPIC_DISTRIBUTION?.series.length === 0}
              nodes={data?.ONE_ON_ONE_AGENDA_TOPIC_DISTRIBUTION.nodes}
              xAxisValues={data?.ONE_ON_ONE_AGENDA_TOPIC_DISTRIBUTION.xAxis}
              series={data?.ONE_ON_ONE_AGENDA_TOPIC_DISTRIBUTION.series}
              xAxisLabel="Months"
              yAxisLabel="Team covered %"
              tooltipTitle="Topic"
              seriesSelectorTitle="Agenda topics"
              emptyStateDescription="Insights on your team's 1:1 performance would appear here once they are setup"
            />
          )}
        </CardContent>
      </Card>
    </OverviewSection>
  );
};
